/**
 * SquareFootage Value Object
 * 
 * Domain concept: "How big is this property?"
 * 
 * Business rules:
 * - Null/undefined normalized to null
 * - Backend sends { min, max } - we preserve that shape
 * 
 * Computed facts:
 * - hasValue: Whether any square footage data exists (min or max > 0)
 * - isRange: Whether min and max differ
 * - value: Single display value (average, or whichever exists)
 * 
 * Usage:
 * - Format: src/ui/sqft/format-square-footage.ts
 * - UI: src/ui/stats/hero-stats.tsx
 */
export class SquareFootage {
  readonly #min: number | null;
  readonly #max: number | null;

  private constructor(min: number | null, max: number | null) {
    this.#min = min;
    this.#max = max;
  }

  /**
   * Creates a SquareFootage from listing data.
   */
  static from(squareFootage: { min: number; max: number } | null | undefined): SquareFootage {
    if (!squareFootage) {
      return new SquareFootage(null, null);
    }
    const { min, max } = squareFootage;
    return new SquareFootage(min || null, max || null);
  }

  /**
   * Whether we have displayable square footage (at least one positive value)
   */
  get hasValue(): boolean {
    return (this.#min !== null && this.#min > 0) || (this.#max !== null && this.#max > 0);
  }

  /**
   * Whether this is a range (both min and max exist and differ)
   */
  get isRange(): boolean {
    return this.#min !== null && this.#max !== null && this.#min !== this.#max;
  }

  get min(): number | null {
    return this.#min;
  }

  get max(): number | null {
    return this.#max;
  }

  /**
   * Single display value: average if range, otherwise whichever exists
   */
  get value(): number | null {
    if (this.#min && this.#max) {
      return Math.ceil((this.#min + this.#max) / 2);
    }
    return this.#min || this.#max;
  }
}
